import { FC, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useAuth } from '../../modules/auth';
import { SensorTransportProvider } from './transports/SensorTransportProvider';
import { useAlarmedBoxes } from './hooks/useAlarmedBoxes';
import { SensorFilters } from './types/sensors';
import { SensorsToolbar } from './components/SensorsToolbar';
import { BoxesGrid } from './components/BoxesGrid';
import { EmptyState } from './components/EmptyState';
import { BoxTimelineDrawer } from './components/BoxTimelineDrawer';
import { clearBoxAlarmsBatch } from './core/_requests';

const initialFilters: SensorFilters = {
  sort: 'gravity_desc', page: 1, items_per_page: 20,
};

const SensorsV2Inner: FC = () => {
  const { currentUser } = useAuth();
  const [filters, setFilters] = useState<SensorFilters>({
    ...initialFilters,
    company_id: currentUser?.last_company_id ? +currentUser.last_company_id : undefined,
  });
  const [selectedBoxId, setSelectedBoxId] = useState<number | null>(null);
  const [refreshSecondsLeft, setRefreshSecondsLeft] = useState(10);

  const { data, isLoading, isFetching } = useAlarmedBoxes(filters);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isFetching) { setRefreshSecondsLeft(10); return; }
    const t = setInterval(() => setRefreshSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [isFetching]);

  const boxes = useMemo(() => data?.data ?? [], [data?.data]);
  const totalsByType = data?.payload?.totals_by_type ?? {};
  const selectedBox = useMemo(
    () => boxes.find((b) => b.box_id === selectedBoxId) ?? null,
    [boxes, selectedBoxId],
  );

  const hasActiveFilters = !!filters.cmb_number || !!filters.alarm_types?.length || !!filters.project_id;

  const handleClearOne = async (box_id: number) => {
    const box = boxes.find((b) => b.box_id === box_id);
    if (!box) return;
    try {
      await clearBoxAlarmsBatch(box_id, box.active_alarms.map((a) => a.type));
      toast.success(`Alarmes limpos para CMB ${box.cmb_number}`);
      queryClient.invalidateQueries(['sensors-v2', 'alarmed-boxes']);
    } catch (e: any) {
      toast.error(`Erro ao limpar: ${e?.message ?? 'desconhecido'}`);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(`Limpar alarmes de ${boxes.length} caixa(s)?`)) return;
    try {
      for (const b of boxes) {
        await clearBoxAlarmsBatch(b.box_id, b.active_alarms.map((a) => a.type));
      }
      toast.success('Alarmes limpos.');
      queryClient.invalidateQueries(['sensors-v2', 'alarmed-boxes']);
    } catch (e: any) {
      toast.error(`Erro ao limpar todos: ${e?.message ?? 'desconhecido'}`);
    }
  };

  return (
    <div style={{ paddingRight: selectedBox ? 380 : 0, transition: 'padding-right 0.2s' }}>
      <SensorsToolbar
        filters={filters}
        totalsByType={totalsByType}
        onChange={(next) => setFilters((prev) => ({ ...prev, ...next }))}
        refreshSecondsLeft={refreshSecondsLeft}
        canClearAll={boxes.length > 0}
        onClearAll={handleClearAll}
      />
      {isLoading && <div className="text-muted">Carregando...</div>}
      {!isLoading && boxes.length === 0 && !hasActiveFilters && <EmptyState variant="no-alarms" />}
      {!isLoading && boxes.length === 0 && hasActiveFilters && (
        <EmptyState variant="no-matches" onClearFilters={() => setFilters(initialFilters)} />
      )}
      {boxes.length > 0 && (
        <BoxesGrid
          boxes={boxes}
          selectedBoxId={selectedBoxId}
          onSelect={(id) => setSelectedBoxId(id)}
          onClearAlarms={handleClearOne}
        />
      )}
      {selectedBox && (
        <BoxTimelineDrawer
          box_id={selectedBox.box_id}
          cmb_number={selectedBox.cmb_number}
          company_name={selectedBox.company_name}
          project_name={selectedBox.project_name}
          onClose={() => setSelectedBoxId(null)}
        />
      )}
    </div>
  );
};

const SensorsV2Page: FC = () => (
  <SensorTransportProvider transport="websocket">
    <SensorsV2Inner />
  </SensorTransportProvider>
);

export { SensorsV2Page };
